import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@app/hooks';

import { OutletRouteForm, OutletRouteRow } from '../types';

type UseOutletRoutesStateOptions = {
  outlet_id?: number;
  initialRoute?: OutletRouteForm;
};

const mapFormToRow = (item: OutletRouteForm): OutletRouteRow => {
  const routeNumberId = Number(item.route_number?.id ?? item.route_number_id);

  return {
    route_number_id: routeNumberId,
    route_number_name: item.route_number?.name || '',
    visits_day: item.visits_day ?? null,
    is_active: item.is_active,
  };
};

const getInitialLocalItems = (route?: OutletRouteForm): OutletRouteForm[] => {
  const routeNumberId = Number(route?.route_number?.id ?? route?.route_number_id);
  if (!route || !routeNumberId) {
    return [];
  }

  return [{
    ...route,
    route_number_id: routeNumberId,
    is_active: route.is_active ?? true,
  }];
};

export const useOutletRoutesState = ({
  outlet_id,
  initialRoute,
}: UseOutletRoutesStateOptions) => {
  const isLocalMode = !outlet_id;

  const [routes, setRoutes] = useState<OutletRouteRow[]>([]);
  const [localItems, setLocalItems] = useState<OutletRouteForm[]>(
    () => getInitialLocalItems(initialRoute)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const api = useApi();
  const routeNamesRef = useRef(new Map<number, string>());
  const didSeedInitialRoute = useRef(getInitialLocalItems(initialRoute).length > 0);

  useEffect(() => {
    if (!isLocalMode || didSeedInitialRoute.current) {
      return;
    }

    const nextItems = getInitialLocalItems(initialRoute);
    if (!nextItems.length) {
      return;
    }

    didSeedInitialRoute.current = true;

    const routeNumberId = nextItems[0].route_number_id;
    if (nextItems[0].route_number?.name) {
      routeNamesRef.current.set(routeNumberId, nextItems[0].route_number.name);
    }

    setLocalItems((items) => {
      const exists = items.some(
        (item) => Number(item.route_number?.id ?? item.route_number_id) === routeNumberId
      );

      return exists ? items : [...nextItems, ...items];
    });
  }, [initialRoute, isLocalMode]);

  useEffect(() => {
    if (!isLocalMode) {
      return;
    }

    setRoutes(localItems.map(mapFormToRow));
  }, [isLocalMode, localItems]);

  const resolveRouteName = useCallback(async (routeNumberId: number) => {
    const cached = routeNamesRef.current.get(routeNumberId);
    if (cached) {
      return cached;
    }

    const response = await api.acb.getRoute(routeNumberId);
    const name = response?.data?.name || String(routeNumberId);
    routeNamesRef.current.set(routeNumberId, name);
    return name;
  }, []);

  const findOutletRouteLink = useCallback(async (
    targetOutletId: number,
    routeNumberId: number
  ) => {
    const response = await api.acb.getOutletRoutes(targetOutletId, {
      limit: 1000,
      offset: 0,
      sort: '+route_number_id',
      filter: {},
    });

    return (response?.data?.data || []).find(
      (item) => item.route_number_id === routeNumberId
    );
  }, []);

  const loadOutletRoutes = useCallback(async () => {
    if (!outlet_id) {
      return [];
    }

    setIsLoading(true);

    const response = await api.acb.getOutletRoutes(outlet_id, {
      limit: 1000,
      offset: 0,
      sort: '+route_number_id',
      filter: { is_active: 'true' },
    });

    const items = response?.data?.data || [];
    const rows = await Promise.all(items.map(async (item) => ({
      route_number_id: item.route_number_id,
      route_number_name: await resolveRouteName(item.route_number_id),
      visits_day: item.visits_day ?? null,
      is_active: item.is_active,
    })));

    setRoutes(rows);
    setIsLoading(false);
    return rows;
  }, [outlet_id, resolveRouteName]);

  useEffect(() => {
    if (isLocalMode) {
      return;
    }

    loadOutletRoutes();
  }, [isLocalMode, loadOutletRoutes]);

  const addOutletRoute = useCallback(async (payload: OutletRouteForm) => {
    if (isLocalMode) {
      setLocalItems((items) => [...items, payload]);
      setIsTouched(true);
      return true;
    }

    if (!outlet_id) {
      return false;
    }

    const routeNumberId = Number(payload.route_number?.id ?? payload.route_number_id);
    if (payload.route_number?.name) {
      routeNamesRef.current.set(routeNumberId, payload.route_number.name);
    }

    const existing = await findOutletRouteLink(outlet_id, routeNumberId);
    const response = existing
      ? await api.acb.updateOutletRoute(outlet_id, routeNumberId, {
        visits_day: payload.visits_day,
        is_active: true,
      })
      : await api.acb.createOutletRoute(outlet_id, {
        route_number_id: routeNumberId,
        visits_day: payload.visits_day,
      });

    if (response?.code === 200 || response?.code === 201) {
      await loadOutletRoutes();
      return true;
    }

    return false;
  }, [findOutletRouteLink, isLocalMode, loadOutletRoutes, outlet_id]);

  const updateOutletRoute = useCallback(async (
    routeNumberId: number,
    payload: OutletRouteForm
  ) => {
    if (isLocalMode) {
      setLocalItems((items) => items.map((item) => (
        Number(item.route_number?.id ?? item.route_number_id) === routeNumberId
          ? payload
          : item
      )));
      setIsTouched(true);
      return true;
    }

    if (!outlet_id) {
      return false;
    }

    const response = await api.acb.updateOutletRoute(outlet_id, routeNumberId, {
      visits_day: payload.visits_day,
    });

    if (response?.code === 200) {
      await loadOutletRoutes();
      return true;
    }

    return false;
  }, [isLocalMode, loadOutletRoutes, outlet_id]);

  const removeOutletRoute = useCallback(async (routeNumberId: number) => {
    if (isLocalMode) {
      setLocalItems((items) => items.filter((item) => (
        Number(item.route_number?.id ?? item.route_number_id) !== routeNumberId
      )));
      setIsTouched(true);
      return true;
    }

    if (!outlet_id) {
      return false;
    }

    const response = await api.acb.deleteOutletRoute(outlet_id, routeNumberId);

    if (response?.code === 200) {
      await loadOutletRoutes();
      return true;
    }

    return false;
  }, [isLocalMode, loadOutletRoutes, outlet_id]);

  const saveOutletRoutes = useCallback(async (targetOutletId: number) => {
    if (!isLocalMode || !localItems.length) {
      return true;
    }

    for (const item of localItems) {
      const response = await api.acb.createOutletRoute(targetOutletId, {
        route_number_id: Number(item.route_number?.id ?? item.route_number_id),
        visits_day: item.visits_day,
      });

      if (response?.code !== 200 && response?.code !== 201) {
        return false;
      }
    }

    setIsTouched(false);
    return true;
  }, [isLocalMode, localItems]);

  return {
    routes,
    isLoading: isLocalMode ? false : isLoading,
    isTouched,
    addOutletRoute,
    updateOutletRoute,
    removeOutletRoute,
    saveOutletRoutes,
  };
};
